import { useCallback, useContext, useState } from "react";
import { FinancialDocumentExtended } from "@common/types/financialDocument";
import { useAddFinancialDocumentsToImporterMutation } from "@api/financialDocument.api";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { pluralize } from "@common/helpers/string";
import { AccrualImporterContext } from "./accrualImporter.context";
import { getAccrualExtraction } from "./accrualImporter.helpers";

function accrualExtractionHasUserEdits(
    financialDocument: FinancialDocumentExtended,
): boolean {
    const extraction = getAccrualExtraction(financialDocument);
    return (
        extraction !== null &&
        "isChangedByUser" in extraction &&
        extraction.isChangedByUser
    );
}

export function useAccrualExtractionRerun() {
    const { importerQueue, documentRows, updateDocumentRow } = useContext(
        AccrualImporterContext,
    );
    const [pendingDocuments, setPendingDocuments] = useState<
        FinancialDocumentExtended[] | null
    >(null);
    const { success, error } = useToaster();
    const rerun = useAddFinancialDocumentsToImporterMutation({
        importerQueue,
        forceRerun: true,
        onSettled: ({ updatedDocuments, failedCount }) => {
            for (const updatedDocument of updatedDocuments) {
                const row = documentRows.find(
                    (documentRow) =>
                        documentRow.financialDocument?.id ===
                        updatedDocument.id,
                );
                if (row) {
                    updateDocumentRow(row.id, {
                        financialDocument: updatedDocument,
                    });
                }
            }
            if (failedCount > 0) {
                error(
                    `Reran ${updatedDocuments.length}. ${failedCount} ${pluralize("draft", failedCount)} failed.`,
                );
            }
        },
    });

    const startRerun = useCallback(
        (financialDocuments: FinancialDocumentExtended[]) => {
            success(
                `Rerunning extraction on ${financialDocuments.length} ${pluralize("draft", financialDocuments.length)}.`,
            );
            rerun.mutate(financialDocuments);
        },
        [rerun, success],
    );

    const requestRerun = useCallback(
        (financialDocuments: FinancialDocumentExtended[]) => {
            if (financialDocuments.length === 0) {
                return;
            }
            if (financialDocuments.some(accrualExtractionHasUserEdits)) {
                setPendingDocuments(financialDocuments);
                return;
            }
            startRerun(financialDocuments);
        },
        [startRerun],
    );

    const rerunConfirm = (
        <ConfirmationModal
            show={pendingDocuments !== null}
            title="Rerun extraction?"
            question="Rerun replaces edits on the selected drafts."
            yes="Rerun"
            yesBtnVariant="destructive"
            no="Cancel"
            onHide={() => setPendingDocuments(null)}
            onConfirm={() => {
                if (!pendingDocuments) {
                    return;
                }
                setPendingDocuments(null);
                startRerun(pendingDocuments);
            }}
        />
    );

    return {
        requestRerun,
        rerunConfirm,
        isRerunning: rerun.isPending,
    };
}
